import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CalculateTotalsOptions,
  CalculateTotalsResponse,
  calculateTotals as universalCalculateTotals,
} from './ecom-v1-totals-calculation-totals-calculator.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function calculateTotals(
  httpClient: HttpClient
): CalculateTotalsSignature {
  return (
    options?: NonNullablePaths<
      CalculateTotalsOptions,
      | `lineItems.${number}._id`
      | `lineItems.${number}.price`
      | `lineItems.${number}.quantity`
      | `selectedMemberships.memberships.${number}._id`
      | `selectedMemberships.memberships.${number}.appId`
      | `selectedMemberships.memberships.${number}.lineItemIds`
      | `selectedShippingOption.code`,
      3
    >
  ) =>
    universalCalculateTotals(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CalculateTotalsSignature {
  /**
   * Returns a totals calculation for specified line items.
   */
  (
    options?: NonNullablePaths<
      CalculateTotalsOptions,
      | `lineItems.${number}._id`
      | `lineItems.${number}.price`
      | `lineItems.${number}.quantity`
      | `selectedMemberships.memberships.${number}._id`
      | `selectedMemberships.memberships.${number}.appId`
      | `selectedMemberships.memberships.${number}.lineItemIds`
      | `selectedShippingOption.code`,
      3
    >
  ): Promise<
    NonNullablePaths<
      CalculateTotalsResponse,
      | `calculatedLineItems`
      | `calculatedLineItems.${number}.lineItemId`
      | `calculatedLineItems.${number}.pricesBreakdown.totalPriceAfterTax.amount`
      | `calculatedLineItems.${number}.pricesBreakdown.totalPriceAfterTax.convertedAmount`
      | `calculatedLineItems.${number}.pricesBreakdown.totalPriceAfterTax.formattedAmount`
      | `calculatedLineItems.${number}.pricesBreakdown.totalPriceAfterTax.formattedConvertedAmount`
      | `calculatedLineItems.${number}.pricesBreakdown.taxDetails.taxRate`
      | `calculatedLineItems.${number}.paymentOption`
      | `calculatedLineItems.${number}.taxableAddress.addressType`
      | `priceSummary.subtotal.amount`
      | `priceSummary.subtotal.convertedAmount`
      | `priceSummary.subtotal.formattedAmount`
      | `priceSummary.subtotal.formattedConvertedAmount`
      | `giftCard._id`
      | `giftCard.obfuscatedCode`
      | `giftCard.amount.amount`
      | `giftCard.amount.convertedAmount`
      | `giftCard.amount.formattedAmount`
      | `giftCard.amount.formattedConvertedAmount`
      | `giftCard.appId`
      | `taxSummary.taxableAmount.amount`
      | `taxSummary.taxableAmount.convertedAmount`
      | `taxSummary.taxableAmount.formattedAmount`
      | `taxSummary.taxableAmount.formattedConvertedAmount`
      | `taxSummary.calculationDetails.manualRateReason`
      | `taxSummary.calculationDetails.autoTaxFallbackDetails.fallbackReason`
      | `taxSummary.calculationDetails.autoTaxFallbackDetails.error.code`
      | `taxSummary.calculationDetails.autoTaxFallbackDetails.error.description`
      | `taxSummary.calculationDetails.rateType`
      | `shippingInfo.region._id`
      | `shippingInfo.region.name`
      | `shippingInfo.selectedCarrierServiceOption.code`
      | `shippingInfo.selectedCarrierServiceOption.title`
      | `shippingInfo.selectedCarrierServiceOption.logistics.pickupDetails.address.streetAddress.number`
      | `shippingInfo.selectedCarrierServiceOption.logistics.pickupDetails.address.streetAddress.name`
      | `shippingInfo.selectedCarrierServiceOption.logistics.pickupDetails.businessLocation`
      | `shippingInfo.selectedCarrierServiceOption.logistics.pickupDetails.pickupMethod`
      | `shippingInfo.selectedCarrierServiceOption.cost.totalPriceAfterTax.amount`
      | `shippingInfo.selectedCarrierServiceOption.cost.totalPriceAfterTax.convertedAmount`
      | `shippingInfo.selectedCarrierServiceOption.cost.totalPriceAfterTax.formattedAmount`
      | `shippingInfo.selectedCarrierServiceOption.cost.totalPriceAfterTax.formattedConvertedAmount`
      | `shippingInfo.selectedCarrierServiceOption.cost.taxDetails.taxRate`
      | `shippingInfo.selectedCarrierServiceOption.cost.taxDetails.rateBreakdown`
      | `shippingInfo.selectedCarrierServiceOption.cost.taxDetails.rateBreakdown.${number}.name`
      | `shippingInfo.selectedCarrierServiceOption.cost.taxDetails.rateBreakdown.${number}.rate`
      | `shippingInfo.selectedCarrierServiceOption.cost.taxDetails.taxBreakdown`
      | `shippingInfo.selectedCarrierServiceOption.cost.taxDetails.taxBreakdown.${number}.jurisdictionType`
      | `shippingInfo.selectedCarrierServiceOption.requestedShippingOption`
      | `shippingInfo.selectedCarrierServiceOption.otherCharges`
      | `shippingInfo.selectedCarrierServiceOption.otherCharges.${number}.type`
      | `shippingInfo.selectedCarrierServiceOption.deliveryAllocations`
      | `shippingInfo.selectedCarrierServiceOption.deliveryAllocations.${number}.deliveryCarrier.code`
      | `shippingInfo.carrierServiceOptions`
      | `shippingInfo.carrierServiceOptions.${number}.carrierId`
      | `appliedDiscounts`
      | `appliedDiscounts.${number}.coupon._id`
      | `appliedDiscounts.${number}.coupon.code`
      | `appliedDiscounts.${number}.coupon.amount.amount`
      | `appliedDiscounts.${number}.coupon.amount.convertedAmount`
      | `appliedDiscounts.${number}.coupon.amount.formattedAmount`
      | `appliedDiscounts.${number}.coupon.amount.formattedConvertedAmount`
      | `appliedDiscounts.${number}.coupon.name`
      | `appliedDiscounts.${number}.discountRule._id`
      | `appliedDiscounts.${number}.discountRule.name.original`
      | `appliedDiscounts.${number}.discountType`
      | `calculationErrors.generalShippingCalculationError.applicationError.code`
      | `calculationErrors.generalShippingCalculationError.applicationError.description`
      | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations`
      | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations.${number}.field`
      | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations.${number}.description`
      | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations.${number}.violatedRule`
      | `calculationErrors.carrierErrors.errors`
      | `calculationErrors.carrierErrors.errors.${number}.carrierId`
      | `calculationErrors.orderValidationErrors`
      | `weightUnit`
      | `currency`
      | `conversionCurrency`
      | `taxIncludedInPrice`
      | `payNow.subtotal.amount`
      | `payNow.subtotal.convertedAmount`
      | `payNow.subtotal.formattedAmount`
      | `payNow.subtotal.formattedConvertedAmount`
      | `payLater.subtotal.amount`
      | `payLater.subtotal.convertedAmount`
      | `payLater.subtotal.formattedAmount`
      | `payLater.subtotal.formattedConvertedAmount`
      | `membershipOptions.eligibleMemberships`
      | `membershipOptions.eligibleMemberships.${number}._id`
      | `membershipOptions.eligibleMemberships.${number}.appId`
      | `membershipOptions.eligibleMemberships.${number}.name.original`
      | `membershipOptions.eligibleMemberships.${number}.credits.total`
      | `membershipOptions.eligibleMemberships.${number}.credits.remaining`
      | `membershipOptions.invalidMemberships`
      | `membershipOptions.invalidMemberships.${number}.reason`
      | `membershipOptions.selectedMemberships`
      | `membershipOptions.selectedMemberships.${number}._id`
      | `membershipOptions.selectedMemberships.${number}.appId`
      | `additionalFees`
      | `additionalFees.${number}.name`
      | `additionalFees.${number}.price.amount`
      | `additionalFees.${number}.price.convertedAmount`
      | `additionalFees.${number}.price.formattedAmount`
      | `additionalFees.${number}.price.formattedConvertedAmount`
      | `additionalFees.${number}.taxDetails.taxRate`
      | `additionalFees.${number}.source`
      | `additionalFees.${number}.taxableAddress.addressType`
      | `payNowTotalAfterGiftCard.amount`
      | `payNowTotalAfterGiftCard.convertedAmount`
      | `payNowTotalAfterGiftCard.formattedAmount`
      | `payNowTotalAfterGiftCard.formattedConvertedAmount`
      | `totalAfterGiftCard.amount`
      | `totalAfterGiftCard.convertedAmount`
      | `totalAfterGiftCard.formattedAmount`
      | `totalAfterGiftCard.formattedConvertedAmount`
      | `payAfterFreeTrial.subtotal.amount`
      | `payAfterFreeTrial.subtotal.convertedAmount`
      | `payAfterFreeTrial.subtotal.formattedAmount`
      | `payAfterFreeTrial.subtotal.formattedConvertedAmount`,
      6
    >
  >;
}

export {
  AdditionalFee,
  AdditionalFeeSource,
  Address,
  AddressLocation,
  AggregatedTaxBreakdown,
  ApiV1AdditionalFee,
  ApplicableLineItems,
  ApplicationError,
  AppliedDiscount,
  AppliedDiscountDiscountSourceOneOf,
  AutoTaxFallbackCalculationDetails,
  BalanceType,
  Benefit,
  BenefitQuotaOneOf,
  CalculateTotalsOptions,
  CalculateTotalsOptionsCouponOneOf,
  CalculateTotalsOptionsGiftCardOneOf,
  CalculateTotalsRequest,
  CalculateTotalsRequestCouponOneOf,
  CalculateTotalsRequestGiftCardOneOf,
  CalculateTotalsResponse,
  CalculatedItemModifier,
  CalculatedLineItem,
  CalculatedPlatformFee,
  CalculationErrors,
  CalculationErrorsShippingCalculationErrorOneOf,
  Carrier,
  CarrierError,
  CarrierErrors,
  CarrierServiceOption,
  CarrierShippingOption,
  CatalogReference,
  ChargeType,
  Coupon,
  CoveredLineItem,
  DataFetchType,
  DeliveryAllocation,
  DeliveryLogistics,
  DeliveryTimeSlot,
  Details,
  DetailsKindOneOf,
  Dimensions,
  DimensionsUnit,
  DiscountRule,
  DiscountRuleName,
  DiscountType,
  ExistingMembership,
  ExistingMembershipSelection,
  ExtendedFields,
  ExternalReference,
  FallbackReason,
  FieldViolation,
  FutureMembershipSelection,
  GiftCard,
  GiftCardCalculationError,
  GiftCardInput,
  Group,
  InvalidMembership,
  ItemCombination,
  ItemCombinationLineItem,
  ItemModifier,
  ItemTaxFullDetails,
  ItemType,
  ItemTypeItemTypeDataOneOf,
  ItemTypePreset,
  JurisdictionType,
  LimitedQuota,
  LimitedQuotaType,
  LineItem,
  LineItemCoverage,
  LineItemDiscount,
  LineItemPricesData,
  ManualCalculationReason,
  Membership,
  MembershipName,
  MembershipOptions,
  MembershipPaymentCredits,
  MembershipSelection,
  MerchantDiscount,
  ModifierGroup,
  MultiCurrencyPrice,
  OtherCharge,
  PaymentOptionType,
  PhysicalProperties,
  PickupDetails,
  PickupMethod,
  PlatformFee,
  PlatformFeeChargeType,
  PlatformFeeSummary,
  PriceSummary,
  PurchasablePlan,
  QuotaType,
  RateType,
  Region,
  RuleType,
  Scope,
  SelectedCarrierServiceOption,
  SelectedCarrierServiceOptionOtherCharge,
  SelectedCarrierServiceOptionPrices,
  SelectedMembership,
  SelectedMemberships,
  SelectedShippingOption,
  ServiceProperties,
  ShippingInformation,
  ShippingOption,
  ShippingPrice,
  ShippingRegion,
  StreetAddress,
  SubscriptionFrequency,
  SubscriptionSettings,
  SystemError,
  TaxBreakdown,
  TaxCalculationDetails,
  TaxCalculationDetailsCalculationDetailsOneOf,
  TaxDetails,
  TaxRateBreakdown,
  TaxSummary,
  TaxableAddress,
  TaxableAddressTaxableAddressDataOneOf,
  TaxableAddressType,
  TotalsCalculationEntity,
  TranslatableString,
  V1AdditionalFee,
  V1Membership,
  V1TaxDetails,
  ValidatedExistingMemberships,
  ValidatedPurchasablePlans,
  ValidationError,
  WeightUnit,
} from './ecom-v1-totals-calculation-totals-calculator.universal.js';
